/**
 * Web Register Page
 * 
 * Clean composition layer using the Template + Wrapper pattern.
 * 
 * Architecture:
 * - Uses AuthLayoutTemplate for the two-column layout
 * - Uses RegisterTemplate for the registration form
 * - Handles authentication logic via useAuthDialog hook
 * - Follows the same pattern as LandingPage.tsx
 * 
 * Benefits:
 * - Minimal code (wrapper delegates to templates)
 * - Single source of truth for auth logic
 * - Easy to test and maintain
 * - Reusable templates
 */

import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthLayoutTemplate, RegisterTemplate, showToast } from '@ideoz/ui';
import { useAuthDialog } from '../hooks/useAuthDialog';
import { useAppSelector } from '@ideoz/core/store';

export default function Register(): JSX.Element {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const authDialog = useAuthDialog();
    const isLoggedIn = useAppSelector((state) => state.app.isLoggedIn);

    // Redirect if already logged in
    React.useEffect(() => {
        if (isLoggedIn) {
            const pairingKey = searchParams.get('pairing_key');
            if (pairingKey) {
                navigate(`/redirect?pairing_key=${pairingKey}`);
            } else {
                navigate('/');
            }
        }
    }, [isLoggedIn, searchParams, navigate]);

    // Handle registration (email/password)
    const handleRegister = useCallback(
        async (name: string, email: string, password: string) => {
            try {
                // Get URL parameters for plugin redirect
                const pairingKey = searchParams.get('pairing_key');
                const figmaSource = searchParams.get('figma_source');
                // Use authDialog's register handler
                // Pass fromChat: false since this is a standalone register page
                await authDialog.handleRegister(name, email, password, { fromChat: false, pairingKey, figmaSource });

                // Success! User will be redirected to onboarding or landing page
                // based on the logic in useAuthDialog
                showToast({
                    message: t('auth.accountCreated'),
                    severity: 'success',
                    type: 'success',
                });
            } catch (error: any) {
                // Error is thrown from useAuthDialog, re-throw to let RegisterTemplate handle it
                throw error;
            }
        },
        [authDialog, searchParams]
    );

    // Handle Google OAuth
    const handleGoogleAuth = useCallback(async () => {
        try {
            // Get URL parameters for plugin redirect
            const pairingKey = searchParams.get('pairing_key');
            const figmaSource = searchParams.get('figma_source');
            await authDialog.handleGoogleAuth({ pairingKey, figmaSource });
            // Success will be handled by the Google auth callback
        } catch (error: any) {
            // Error is thrown from useAuthDialog, re-throw to let RegisterTemplate handle it
            throw error;
        }
    }, [authDialog, searchParams]);

    // Navigate to login page
    const handleNavigateToLogin = useCallback(() => {
        // Preserve URL parameters when switching to login
        const pairingKey = searchParams.get('pairing_key');
        const figmaSource = searchParams.get('figma_source');

        if (pairingKey || figmaSource) {
            const params = new URLSearchParams();
            if (pairingKey) params.set('pairing_key', pairingKey);
            if (figmaSource) params.set('figma_source', figmaSource);
            navigate(`/login?${params.toString()}`);
        } else {
            navigate('/login');
        }
    }, [navigate, searchParams]);

    return (
        <AuthLayoutTemplate isWeb={true}>
            <RegisterTemplate
                onRegister={handleRegister}
                onGoogleAuth={handleGoogleAuth}
                onNavigateToLogin={handleNavigateToLogin}
                isLoading={authDialog.isLoading}
                isWeb={true}
                initialError={authDialog.error}
            />
        </AuthLayoutTemplate>
    );
}
